import React, { useState, useEffect, useCallback, useMemo, useRef, useLayoutEffect } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import GlobalStyle from './GlobalStyle';
import AddCorr from './AddCorr';
import debounce from "lodash/debounce";
import { FaTrash } from "react-icons/fa";
import { createPortal } from "react-dom";
import TextareaAutosize from "react-textarea-autosize";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  width: 100%;
  max-width: 100%;
  overflow: visible;
  position: relative;
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  background-color: #f4f4f4;
  flex-grow: 1;
  overflow: visible;
  position: relative;
`;

const AutocompleteWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const MainLayout = styled.div`
  display: flex;
  height: 100vh;
  overflow: visible;
`;

const CheckboxWrapper = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CellWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fdfdfd;
  table-layout: fixed;
  word-break: break-word;
`;

const Th = styled.th`
  background-color: #f1f3f5;
  padding: 10px;
  border: 1px solid #ddd;
  text-align: left;
`;

const Td = styled.td`
  padding: 10px;
  border: 1px solid #ddd;
  white-space: normal;
  word-break: break-word;
  overflow: visible;
  max-width: none;
  vertical-align: top;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
  gap: 10px;
`;

const DriverColorDot = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${(props) => props.color || '#ccc'};
  margin-right: 8px;
  vertical-align: middle;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalContent = styled.div`
  background: white;
  padding: 20px;
  max-width: 500px;
  max-height: 70vh;
  overflow-y: auto;
  border-radius: 8px;
`;


const Input = styled.input`
  padding: 8px;
  border-radius: 4px;
  border: 1px solid #ccc;
  flex: 1;
  width: 100%;
  resize: vertical;
  min-height: 38px;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: break-word;
`;

const Button = styled.button`
  padding: 10px 20px;
  margin-top: 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  margin-right: 10px;

  &:hover {
    background-color: #0056b3;
  }
`;

const DeleteButton = styled(Button)`
  height: 38px;
  padding: 0 12px;
  font-size: 16px;
  background-color: #dc3545;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  margin: 0;
  position: static;

  &:hover {
    background-color: #c82333;
  }
`;

const TableWrapper = styled.div`
  overflow-x: auto;
`;

const MobileCards = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const TopButtons = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 30px;
`;

const FiltersContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
`;

const StyledTextarea = styled(TextareaAutosize)`
  width: 100%;
  padding: 6px;
  font-size: 1rem;
  font-family: inherit;
  border-radius: 4px;
  border: 1px solid #ccc;
  box-sizing: border-box;
  resize: none;
`;


const EditProducts = () => {
const excludedFields = ["customer", "orderDate", "unit", "code", "onlyDate", "formattedOrderId", "existingOrderId"];
const fieldLabels = {
   systemquantity: "Qt.",
   purchasePrice: "Prz Acq.",
   selectedSalePrice: "Prz Ven.",
   partialPrice: "Parz. ",
   productName: "Prodotto",
   alternativeQuantity: "Qt. 2",
   lotto: "Lotto",
   quantity: "Qta"
  };
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [customer, setCustomer] = useState('');
  const [orderDate, setOrderDate] = useState('');
  const [onlyDate, setOnlyDate] = useState('');
  const [pagato, setPagato] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [lastTypedProductName, setLastTypedProductName] = useState('');
  const [allSuggestions, setAllSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [inputValue, setInputValue] = useState("");  // valore input attuale
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [currentProductIndex, setCurrentProductIndex] = useState(null);
  const inputRefs = useRef({});
  const dropdownRef = useRef(null);

// gestire click fuori da dropdown
useEffect(() => {
  const handleClickOutside = (event) => {
    const inputEl = inputRefs.current[currentProductIndex];
    const dropdownEl = dropdownRef.current;
    if (
      inputEl &&
      !inputEl.contains(event.target) &&
      dropdownEl &&
      !dropdownEl.contains(event.target)
    ) {
      setDropdownVisible(false);
    }
  };
  document.addEventListener("mousedown", handleClickOutside);
  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, [currentProductIndex]);

// useProductAutocomplete
function useProductAutocomplete(orderId) {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasResults, setHasResults] = useState(false);
  const fetchFilteredProducts = useMemo(() => 
    debounce(async (filter) => {
      if (!filter || filter.trim().length < 3) {
        setSuggestions([]);
        setHasResults(false);
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data } = await axios.get(
          `/api/products?filter=${encodeURIComponent(filter)}&IdForSearch=${orderId}`
        );
        // Ordina e mappa i risultati per la UI
        const sorted = [...data].sort((a, b) => {
          if (a.source === "internal" && b.source !== "internal") return -1;
          if (a.source !== "internal" && b.source === "internal") return 1;
          return 0;
        });
        const combined = sorted.map(p => {
          const code = (p.code || p.productCode || "").trim();
          const name = (p.productName || "").trim();
          const ARUNMIS1 = (p.ARUNMIS1 || "").trim();
          const ARUNMIS2 = (p.ARUNMIS2 || "").trim();
          let dateInfo = "";
          if (p.source === "internal" && p.date) {
            dateInfo = ` (${p.date})`;
          }
          return {
            ...p,
            ARCODART: code,
            ARDESART: name,
            ARUNMIS1,
            ARUNMIS2,
            combinedDescription: `${code} ${name}${dateInfo}`,
          };
        });
        setSuggestions(combined);
        setHasResults(combined.length > 0);
      } catch (err) {
        console.error("Errore durante fetch prodotti:", err);
        setSuggestions([]);
        setHasResults(false);
      } finally {
        setLoading(false);
      }
    }, 400), 
  [orderId]);

  // Pulizia debounce al dismount
  useEffect(() => {
    return () => fetchFilteredProducts.cancel();
  }, [fetchFilteredProducts]);

  return { suggestions, loading, hasResults, fetchFilteredProducts, setSuggestions };
}

// Dropdown portale per posizionare dropdown sotto input
const DropdownPortal = ({ anchorRef, children, isOpen, onClickOutside }) => {
  const [position, setPosition] = useState({ top: 0, left: 0, width: 0 });
  const portalRef = useRef(document.createElement("div"));
  useLayoutEffect(() => {
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    setPosition({
      top: rect.bottom,
      left: rect.left,
      width: rect.width
    });
    document.body.appendChild(portalRef.current);
    return () => {
      if (portalRef.current?.parentNode) {
        portalRef.current.parentNode.removeChild(portalRef.current);
      }
    };
  }, [anchorRef]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdownEl = portalRef.current;
      const anchorEl = anchorRef?.current;
      if (
        dropdownEl &&
        !dropdownEl.contains(event.target) &&
        anchorEl &&
        !anchorEl.contains(event.target)
      ) {
        onClickOutside?.(); // chiama la callback se clic fuori
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, anchorRef, onClickOutside]);
  if (!isOpen) return null;
  return createPortal(
    <div
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: position.width,
        backgroundColor: "#fff",
        border: "1px solid #ccc",
        zIndex: 1000,
        boxShadow: "0 4px 6px rgba(0,0,0,0.1)"
      }}
    >
      {children}
    </div>,
    portalRef.current
  );
};
// ############################################################


const handleSelectSuggestion = (selected) => {
  if (currentProductIndex === null) return;
  const updatedProducts = [...products];
  const target = updatedProducts[currentProductIndex];
  target.productName = selected.combinedDescription.replace(/\s*\(.*?\)\s*$/, "").trim();
  target.code = selected.ARCODART;
  target.unit = selected.ARUNMIS1;
  setProducts(updatedProducts);
  setDropdownVisible(false);
};

const {
  fetchFilteredProducts,
  suggestions,
  clearSuggestions,
  hasResults,
} = useProductAutocomplete(orderId);

useEffect(() => {
  if (inputValue.trim().length < 3) {
    setDropdownVisible(false);
    return;
  }
  fetchFilteredProducts(inputValue, orderId); // <-- passa idSearch qui!
  setDropdownVisible(true);
}, [inputValue, orderId, fetchFilteredProducts]);
// ###############################################

useEffect(() => {
    axios.get(`/api/corrs/${encodeURIComponent(orderId)}`)
      .then((res) => {
        const orderDateRaw = res.data.orderDate || '';
        const onlyDateVal = orderDateRaw.includes("T") ? orderDateRaw.split("T")[0] : orderDateRaw;
	setPagato((res.data.pagato || "").trim().toLowerCase() === "si");
        const cleaned = (res.data.products || []).map(p => ({
          ...p,
          productName: p.productName.trim().replace(/\s+/g, " ") || "",
          customer: res.data.customer,
          formattedOrderId: res.data.orderId,
          existingOrderId: res.data.orderId,
          partialPrice: typeof p.partialPrice === "string" && p.partialPrice.includes("€")
            ? p.partialPrice
            : `${p.partialPrice}\u00A0€`,
        }));
        setProducts(cleaned);
        setCustomer(res.data.customer || '');
        setOrderDate(orderDateRaw);
        setOnlyDate(onlyDateVal);
      })
      .catch((err) => {
        console.error("Errore nel recupero dei prodotti:", err);
      });
  }, [orderId]);

  const handleDeleteProduct = (indexToDelete) => {
    const updated = products.filter((_, idx) => idx !== indexToDelete);
    setProducts(updated);
  };

  const handleSave = () => {
    axios.post(`/api/corrs/update-products`, {
      orderId,
      products,
      pagato: pagato ? "si" : "no",
    })
      .then(() => {
        alert("Prodotti aggiornati con successo!");
        navigate("/corrsedit");
      })
      .catch((err) => {
        console.error("Errore durante il salvataggio dei prodotti:", err);
        alert("Errore durante il salvataggio.");
      });
  };
  const onAddCorrSuccess = (addedProduct) => {
    setProducts(prev => [...prev, addedProduct]);
    setShowAddForm(false);
  };
const handleProductChange = (index, key, value) => {
  setProducts(prevProducts => {
    const newProducts = [...prevProducts];
    newProducts[index] = {
      ...newProducts[index],
      [key]: value,
    };
    return newProducts;
  });
};

const handleProductFieldChange = (index, field, value) => {
  // se il campo è quantity o selectedSalePrice, converti automaticamente la virgola in punto
  let newValue = value;
  if (field === "quantity" || field === "selectedSalePrice") {
    newValue = value.replace(/,/g, "."); // tutte le virgole diventano punti
  }
  const updated = [...products];
  updated[index][field] = newValue;
  // calcolo nuovo parziale solo se i campi interessati sono quantità o prezzo vendita
  const qty = parseFloat(
    field === "quantity" ? newValue :
    updated[index].quantity?.toString().replace(",", ".") ?? "0"
  ) || 0;
  const price = parseFloat(
    field === "selectedSalePrice"
      ? newValue
      : updated[index].selectedSalePrice?.toString().replace("€", "").replace(",", ".").trim() ?? "0"
  ) || 0;
  if (["quantity", "selectedSalePrice"].includes(field)) {
    const partial = (qty * price).toFixed(2);
    updated[index].partialPrice = `${partial}\u00A0€`;
  }
  setProducts(updated);
};


const editableFields = [
  "systemquantity",
  "alternativeQuantity",
  "purchasePrice",
  "selectedSalePrice",
  "quantity",
  "lotto",
  "quantity"
];

const orderTotal = products.reduce((acc, p) => {
  const partialRaw = typeof p.partialPrice === "string"
    ? p.partialPrice.replace("€", "").replace(",", ".").trim()
    : "0";
  const value = parseFloat(partialRaw) || 0;
  return acc + value;
}, 0).toFixed(2);

const [isMobile, setIsMobile] = useState(window.innerWidth <= 1366);
useEffect(() => {
  const handleResize = () => {
    setIsMobile(window.innerWidth <= 1366);
  };
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

const cleanDisplayValue = (val) => {
  if (typeof val === "string") {
    const trimmed = val.trim().toLowerCase();
    // se è proprio "null" o inizia con "null "
    if (trimmed === "null" || trimmed.startsWith("null ")) {
      return "";
    }
  }
  return val ?? "";
};


// ######### RETURN
  return (
    <>
      <PageContainer>
        <Content>
          <h2>Modifica Prodotti o aggiunta a corrispettivo </h2>
	  <p style={{ fontSize: "0.9rem", color: "#dc3545", marginTop: "4px" }}>
      ⚠️ Attenzione - ricordarsi di premere il bottone "Salva modifiche" dopo aver eliminato o modificato un prodotto. </p>


{!showAddForm && (
  <>
    <Button onClick={() => setShowAddForm(true)}>Aggiungi prodotto</Button>
    <div style={{ marginLeft: "15px", userSelect: "none" }}>
      <label>
        <input
          type="checkbox"
          checked={pagato}
          onChange={(e) => setPagato(e.target.checked)}
          style={{ marginRight: "6px" }}
        />
        Pagato
      </label>
      <div style={{ fontWeight: "bold", marginTop: "4px" }}>
        Numero articoli: {products.length}
      </div>
    </div>
  </>
)}

          {showAddForm && (
            <AddCorr
              orderId={orderId}
              formattedOrderId={orderId}
              existingOrderId={orderId}
              customer={customer}
              onlyDate={onlyDate}
              orderDate={orderDate}
              isFirstProduct={false}
              onSuccess={onAddCorrSuccess}
              onCancel={() => setShowAddForm(false)}
	      returnToEditProducts={true}
            />
          )}
          {!showAddForm && (
            <>
              {products.length === 0 ? (
                <p>Nessun prodotto da mostrare</p>
              ) : (

      <>
        {isMobile ? (
        <MobileCards>
          {products.map((product, index) => (
            <div
              key={index}
              style={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "12px",
                marginBottom: "12px",
                backgroundColor: "#fff",
                boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
              }}
            >
              {Object.entries(product)
                .filter(([key]) => !excludedFields.includes(key))
                .map(([key, value]) => (
                  <div key={key} style={{ marginBottom: "10px" }}>
                    <div style={{ fontWeight: 600, marginBottom: "4px" }}>
                      {fieldLabels[key] || key}
                    </div>
                    {key === "productName" ? (
                      <AutocompleteWrapper>
                        <StyledTextarea
                          ref={(el) => (inputRefs.current[index] = el)}
                          value={product[key]}
                          minRows={1}
                          maxRows={6}
                          onChange={(e) => {
                            handleProductFieldChange(index, key, e.target.value);
                            setInputValue(e.target.value);
                            setCurrentProductIndex(index);
                            setDropdownVisible(true);
                          }}
                          onFocus={() => {
                            setInputValue(product[key]);
                            setCurrentProductIndex(index);
                            if (product[key].trim().length > 1) {
                              fetchFilteredProducts(product[key]);
                              setDropdownVisible(true);
                            }
                          }}
                        />
                        {dropdownVisible && currentProductIndex === index && hasResults && (
                          <DropdownPortal
                            anchorRef={{ current: inputRefs.current[index] }}
                            isOpen={true}
                            onClickOutside={() => setDropdownVisible(false)}
                          >
                            <ul style={{ listStyle: "none", margin: 0, padding: 0, maxHeight: "200px", overflowY: "auto" }}>
                              {suggestions.map((item, idx) => (
                                <li
                                  key={idx}
                                  style={{ padding: "8px", cursor: "pointer", borderBottom: "1px solid #ccc" }}
                                  onClick={() => handleSelectSuggestion(item)}
                                >
                                  {item.combinedDescription}
                                </li>
                              ))}
                            </ul>
                          </DropdownPortal>
                        )}
                      </AutocompleteWrapper>
                    ) : key === "partial" ? (
                      <StyledTextarea value={value ?? ""} readOnly minRows={1} maxRows={6} />
                    ) : editableFields.includes(key) ? (
                      <StyledTextarea
                        minRows={1}
                        maxRows={6}
                        value={cleanDisplayValue(value)}
                        onChange={(e) =>
                          handleProductFieldChange(index, key, e.target.value)
                        }
                      />
                    ) : (
                      <div>{value}</div>
                    )}
                  </div>
                ))}
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <DeleteButton
                  onClick={() => {
                    if (window.confirm("Sei sicuro di voler eliminare il prodotto selezionato?")) {
                      handleDeleteProduct(index);
                    }
                  }}
                >
                  <FaTrash color="white" size={16} />
                </DeleteButton>
              </div>
            </div>
          ))}
        </MobileCards>
        ) : (

		 <TableWrapper>
                  <Table>
                    <thead>
                      <tr>
                        {Object.keys(products[0])
                          .filter((key) => !excludedFields.includes(key))
                          .map((key) => (
                            <Th key={key}>{fieldLabels[key] || key}</Th>
                          ))}
                        <Th> </Th>
                      </tr>
                    </thead>
       <tbody>
        {products.map((product, index) => (
          <tr key={index}>
   {Object.entries(product)
    .filter(([key]) => !excludedFields.includes(key))
    .map(([key, value]) => (
    <Td
      key={key}
      style={{
        position: "relative",
        overflow: "visible",
        whiteSpace: "normal",
        wordBreak: "break-word",
      }}
      >
      {key === "productName" ? (
        <AutocompleteWrapper>
          <StyledTextarea
            ref={(el) => (inputRefs.current[index] = el)}
            value={product[key]}
            minRows={1}
            maxRows={6}
            onChange={(e) => {
              handleProductFieldChange(index, key, e.target.value);
              setInputValue(e.target.value);
              setCurrentProductIndex(index);
              setDropdownVisible(true);
            }}
            onFocus={() => {
              setInputValue(product[key]);
              setCurrentProductIndex(index);
              if (product[key].trim().length > 1) {
                fetchFilteredProducts(product[key]);
                setDropdownVisible(true);
              }
            }}
          />
          {dropdownVisible && currentProductIndex === index && hasResults && (
            <DropdownPortal
              anchorRef={{ current: inputRefs.current[index] }}
              isOpen={true}
              onClickOutside={() => setDropdownVisible(false)}
            >
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {suggestions.map((item, idx) => (
                  <li
                    key={idx}
                    style={{
                      padding: "8px",
                      cursor: "pointer",
                      borderBottom: "1px solid #ccc",
                    }}
                    onClick={() => handleSelectSuggestion(item)}
                  >
                    {item.combinedDescription}
                  </li>
                ))}
              </ul>
            </DropdownPortal>
          )}
        </AutocompleteWrapper>
      ) : key === "partial" ? (
        <StyledTextarea
          value={value ?? ""}
          readOnly
          minRows={1}
          maxRows={6}
        />
      ) : editableFields.includes(key) ? (
        <StyledTextarea
          minRows={1}
          maxRows={6}
          value={value ?? ""}
          onChange={(e) => handleProductFieldChange(index, key, e.target.value)}
        />
      ) : (
        value
      )}
     </Td>
    ))}
   <Td>
     <DeleteButton
      onClick={() => {
      const confirmDelete = window.confirm("Sei sicuro di voler eliminare il prodotto selezionato?");
      if (confirmDelete) {
         handleDeleteProduct(index);
        }
        }}
       >
      <FaTrash color="white" size={16} />
     </DeleteButton>
      </Td>
     </tr>
     ))}
    </tbody>
   </Table>
  </TableWrapper>
  )}
        <div style={{
         textAlign: "right",
         marginTop: "10px",
         fontSize: "1.1rem",
         fontWeight: "bold"
        }}>
        Totale ordine: {orderTotal} €
        </div>
        <Button onClick={handleSave} style={{ marginTop: "15px" }}>
        Salva modifiche
        </Button>
      </>
     )}
     </>
     )}
   </Content>
 </PageContainer>
</>
);
};

export default EditProducts;
